import { Commitment, Deal } from "@ifgengineering/client-invest-sdk";
import { Currency, ExchangeRates } from "../types";

interface PortfolioValue {
  gbp: number;
  usd: number;
  totalToGBP: number;
  totalToUSD: number;
  valueByCurrency: number;
}

export const getTotalPortfolioValueInCurrency = ({
  gbpValue,
  usdValue,
  exchangeRates,
}: {
  gbpValue: number;
  usdValue: number;
  exchangeRates: ExchangeRates;
}): { GBP: number; USD: number } => {
  const totalValueInGBP = gbpValue + usdValue * exchangeRates.USD_TO_GBP;
  const totalValueInUSD = usdValue + gbpValue * exchangeRates.GBP_TO_USD;
  return {
    GBP: totalValueInGBP,
    USD: totalValueInUSD,
  };
};

export const getCapitalGrowthValue = ({
  gbpValue,
  usdValue,
  pvEnabledDeals,
}: {
  gbpValue: number;
  usdValue: number;
  pvEnabledDeals: number;
}): { GBP: number; USD: number } => {
  const totalValueInGBP = (gbpValue + usdValue) / pvEnabledDeals;
  const totalValueInUSD = (usdValue + gbpValue) / pvEnabledDeals;
  return {
    GBP: totalValueInGBP,
    USD: totalValueInUSD,
  };
};

export const getTotalPortfolioValueInUSD = ({
  gbpValue,
  usdValue,
  exchangeRates,
}: {
  gbpValue: number;
  usdValue: number;
  exchangeRates: ExchangeRates;
}): number => {
  return usdValue + gbpValue * exchangeRates.GBP_TO_USD;
};

export interface ReshapedPortfolioData {
  data: Commitment[];
  value: PortfolioValue;
  growth: PortfolioValue;
  earnings: PortfolioValue;
  reservedForInvestment: PortfolioValue;
}

export const reduceByCurrencyAndKey = ({
  commitments,
  currency,
  key,
  deals,
}: {
  commitments: Commitment[];
  currency: Currency;
  key:
    | "currentInvestmentAmount"
    | "earnings"
    | "reservedForInvestment"
    | "capitalGrowth";
  deals?: Deal[];
}) => {
  return commitments
    .filter((commitment) => commitment.currency === currency)
    .reduce((acc, commitment) => {
      if (key === "currentInvestmentAmount") {
        const deal = deals?.find((deal) => deal.id === commitment.dealId);
        const value = deal?.enablePortfolioV1_5
          ? commitment.availableInvestmentValue
          : commitment.currentInvestmentAmount;
        return acc + value;
      }

      const value = commitment[key] || 0;
      return acc + value;
    }, 0);
};

const getTotalValue = (
  commitments: Commitment[],
  deals: Deal[],
  exchangeRates: ExchangeRates,
  currency?: Currency
): PortfolioValue => {
  const gbpOnlyValue = reduceByCurrencyAndKey({
    commitments,
    currency: "GBP",
    key: "currentInvestmentAmount",
    deals,
  });
  const usdOnlyValue = reduceByCurrencyAndKey({
    commitments,
    currency: "USD",
    key: "currentInvestmentAmount",
    deals,
  });
  const totalValue = getTotalPortfolioValueInCurrency({
    gbpValue: gbpOnlyValue,
    usdValue: usdOnlyValue,
    exchangeRates,
  });
  const value =
    currency === "GBP"
      ? gbpOnlyValue
      : currency === "USD"
      ? usdOnlyValue
      : totalValue.GBP;

  return {
    gbp: gbpOnlyValue,
    usd: usdOnlyValue,
    totalToGBP: totalValue.GBP,
    totalToUSD: totalValue.USD,
    valueByCurrency: value,
  };
};

const getTotalEarnings = (
  commitments: Commitment[],
  exchangeRates: ExchangeRates,
  currency?: Currency
): PortfolioValue => {
  const gbpOnlyEarning = reduceByCurrencyAndKey({
    commitments,
    currency: "GBP",
    key: "earnings",
  });
  const usdOnlyEarnings = reduceByCurrencyAndKey({
    commitments,
    currency: "USD",
    key: "earnings",
  });
  const totalEarnings = getTotalPortfolioValueInCurrency({
    gbpValue: gbpOnlyEarning,
    usdValue: usdOnlyEarnings,
    exchangeRates,
  });
  const value =
    currency === "GBP"
      ? gbpOnlyEarning
      : currency === "USD"
      ? usdOnlyEarnings
      : totalEarnings.GBP;

  return {
    gbp: gbpOnlyEarning,
    usd: usdOnlyEarnings,
    totalToGBP: totalEarnings.GBP,
    totalToUSD: totalEarnings.USD,
    valueByCurrency: value,
  };
};

const getTotalReservedForInvestment = (
  commitments: Commitment[],
  exchangeRates: ExchangeRates,
  currency?: Currency
): PortfolioValue => {
  const gbpOnlyReservedForInvestment = reduceByCurrencyAndKey({
    commitments,
    currency: "GBP",
    key: "reservedForInvestment",
  });
  const usdOnlyReservedForInvestment = reduceByCurrencyAndKey({
    commitments,
    currency: "USD",
    key: "reservedForInvestment",
  });
  const totalReservedForInvestment = getTotalPortfolioValueInCurrency({
    gbpValue: gbpOnlyReservedForInvestment,
    usdValue: usdOnlyReservedForInvestment,
    exchangeRates,
  });
  const value =
    currency === "GBP"
      ? gbpOnlyReservedForInvestment
      : currency === "USD"
      ? usdOnlyReservedForInvestment
      : totalReservedForInvestment.GBP;

  return {
    gbp: gbpOnlyReservedForInvestment,
    usd: usdOnlyReservedForInvestment,
    totalToGBP: totalReservedForInvestment.GBP,
    totalToUSD: totalReservedForInvestment.USD,
    valueByCurrency: value,
  };
};

const getTotalGrowth = (
  commitments: Commitment[],
  deals: Deal[],
  currency?: Currency
): PortfolioValue => {
  const gbpOnlyGrowth = reduceByCurrencyAndKey({
    commitments,
    currency: "GBP",
    key: "capitalGrowth",
  });
  const usdOnlyGrowth = reduceByCurrencyAndKey({
    commitments,
    currency: "USD",
    key: "capitalGrowth",
  });

  const pvEnabledDeals = commitments.filter((c) => {
    const deal = deals?.find((deal) => deal.id === c.dealId);
    return deal?.enablePortfolioV1_5;
  }).length;

  const totalGrowth = getCapitalGrowthValue({
    gbpValue: gbpOnlyGrowth,
    usdValue: usdOnlyGrowth,
    pvEnabledDeals,
  });
  const value =
    currency === "GBP"
      ? gbpOnlyGrowth
      : currency === "USD"
      ? usdOnlyGrowth
      : totalGrowth.GBP;

  return {
    gbp: gbpOnlyGrowth,
    usd: usdOnlyGrowth,
    totalToGBP: totalGrowth.GBP,
    totalToUSD: totalGrowth.USD,
    valueByCurrency: value,
  };
};

export const reshapePortfolioData = ({
  commitments,
  deals,
  exchangeRates,
  currency,
}: {
  commitments: Commitment[];
  deals: Deal[];
  exchangeRates: ExchangeRates;
  currency?: Currency;
}): ReshapedPortfolioData => {
  const value = getTotalValue(commitments, deals, exchangeRates, currency);
  const earnings = getTotalEarnings(commitments, exchangeRates, currency);
  const reservedForInvestment = getTotalReservedForInvestment(
    commitments,
    exchangeRates,
    currency
  );

  const growth = getTotalGrowth(commitments, deals);
  return {
    data: commitments,
    value,
    earnings,
    growth,
    reservedForInvestment,
  };
};
